import React from "react";
import NavBar from "../components/Navbar/NavBar";
import Footer from "../components/Footer";
import { useDocTitle } from "../components/CustomHook";
import { Link } from "react-router-dom";

/*
 * The Network — the big-picture page presenting the ZelTechnology
 * ecosystem: four pillars, the flywheel, and how participation makes
 * everything stronger.
 */

const pillars = [
  {
    icon: "🤝",
    name: "ZelConnect",
    tag: "People helping people with technology",
    desc: "Members request assistance, LAMNA handles the straightforward issues, and verified technicians accept the cases that need a human. Remote sessions, tickets, and a community that shows up.",
    status: "Live",
    link: "/remote",
    cta: "Get or give help",
    color: "#12bfbe",
  },
  {
    icon: "🛡️",
    name: "ZelGuardian",
    tag: "A personal technology protection companion",
    desc: "Powered by our AI, LAMNA — helping members recognize scams before they strike, understand security risks, maintain backups, and recover from problems. Protection, not just repair.",
    status: "Live — preview",
    link: "/scam-check",
    cta: "Meet your guardian",
    color: "#9198e5",
  },
  {
    icon: "🎓",
    name: "ZelPath",
    tag: "From assistance to opportunity",
    desc: "Someone comes for computer help, discovers free learning, completes practical lessons, and eventually qualifies to help others. Assistance becomes skills; skills become opportunity.",
    status: "Launching",
    link: "/learn",
    cta: "Start your path",
    color: "#1e3a8a",
  },
  {
    icon: "📊",
    name: "ZelImpact",
    tag: "Transparent funding for real outcomes",
    desc: "Donors support specific projects, follow verified milestones, and see what happened after — real computers, real workshops, real people helped. Accountability meets human outcomes.",
    status: "Live",
    link: "/transparency",
    cta: "See the impact",
    color: "#36395a",
  },
];

const flywheel = [
  { step: "1", text: "Someone receives help", icon: "🤝" },
  { step: "2", text: "They learn and grow", icon: "🎓" },
  { step: "3", text: "They help someone else", icon: "🔗" },
  { step: "4", text: "Their work attracts support", icon: "💙" },
  { step: "5", text: "The next person gets help", icon: "🦈" },
];

const Network = () => {
  useDocTitle("ZelT | The Network");

  return (
    <div>
      <NavBar />
      <div className="mt-8 w-full bg-white py-12 lg:py-20">
        <div className="mx-4 max-w-6xl mx-auto">
          {/* hero */}
          <div className="text-center mb-14" data-aos="zoom-in">
            <span className="inline-block px-4 py-1 rounded-full text-xs font-bold bg-teal-100 text-blue-900 border border-teal-400 mb-4">
              THE BIG PICTURE
            </span>
            <h1 className="text-4xl md:text-5xl font-extrabold text-blue-900 mb-4">
              ZelTechnology: A Community-Powered
              <br />
              Technology Assistance Network
            </h1>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto leading-relaxed">
              Not simply an IT support nonprofit. One place where people get
              technology help, learn, contribute — and eventually help someone
              else. Four pillars, one mission, powered by people.
            </p>
          </div>

          {/* four pillars */}
          <div className="grid gap-6 md:grid-cols-2 mb-16">
            {pillars.map((p) => (
              <div
                key={p.name}
                className="relative bg-gray-50 rounded-2xl p-6 shadow-lg border-t-4 flex flex-col"
                style={{ borderTopColor: p.color }}
                data-aos="fade-up"
              >
                <span className="absolute top-4 right-4 px-3 py-1 rounded-full text-xs font-bold bg-white border border-gray-200 text-gray-500">
                  {p.status}
                </span>
                <span className="text-4xl mb-3">{p.icon}</span>
                <h2 className="text-2xl font-extrabold text-blue-900">{p.name}</h2>
                <p className="text-teal-600 font-semibold text-sm mb-3">{p.tag}</p>
                <p className="text-gray-600 text-sm leading-relaxed flex-1 mb-4">
                  {p.desc}
                </p>
                <Link
                  to={p.link}
                  className="self-start px-4 py-2 rounded-xl bg-blue-900 text-white text-sm font-bold hover:bg-blue-800 transition"
                >
                  {p.cta} →
                </Link>
              </div>
            ))}
          </div>

          {/* the flywheel */}
          <div
            className="rounded-3xl p-8 md:p-12 text-white shadow-2xl mb-16"
            style={{ background: "linear-gradient(160deg, #0f172a, #1e3a8a)" }}
            data-aos="fade-up"
          >
            <div className="text-center mb-8">
              <h2 className="text-3xl font-extrabold mb-3">The Flywheel</h2>
              <p className="text-gray-300 max-w-2xl mx-auto">
                The network becomes more valuable as more people participate.
                Every person who receives help can become someone who gives
                it.
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-5 gap-4 text-center">
              {flywheel.map((f) => (
                <div key={f.step} className="relative">
                  <div className="bg-white/10 rounded-2xl p-4 border border-white/10 h-full">
                    <p className="text-3xl mb-2">{f.icon}</p>
                    <p className="text-teal-300 text-xs font-bold mb-1">
                      STEP {f.step}
                    </p>
                    <p className="text-white text-sm font-semibold">{f.text}</p>
                  </div>
                  {f.step !== "5" && (
                    <span className="hidden sm:block absolute top-1/2 -right-5 text-teal-400 text-xl">
                      →
                    </span>
                  )}
                </div>
              ))}
            </div>
            <p className="text-center text-teal-300 text-sm font-bold mt-8">
              ↺ …and the loop continues
            </p>
          </div>

          {/* join the network */}
          <div className="grid gap-6 sm:grid-cols-3 mb-8" data-aos="fade-up">
            <Link
              to="/membership"
              className="bg-gray-50 rounded-2xl p-6 text-center shadow-md hover:shadow-xl transition border-t-4 border-teal-400"
            >
              <p className="text-3xl mb-2">🙋</p>
              <h3 className="font-extrabold text-blue-900 mb-1">I need help</h3>
              <p className="text-gray-500 text-sm mb-3">
                Free membership, real assistance.
              </p>
              <p className="text-blue-900 font-bold text-sm">Join free →</p>
            </Link>
            <Link
              to="/contact?request=volunteer"
              className="bg-gray-50 rounded-2xl p-6 text-center shadow-md hover:shadow-xl transition border-t-4 border-purple-300"
            >
              <p className="text-3xl mb-2">🛠️</p>
              <h3 className="font-extrabold text-blue-900 mb-1">I can help</h3>
              <p className="text-gray-500 text-sm mb-3">
                Technicians and future volunteers.
              </p>
              <p className="text-blue-900 font-bold text-sm">Apply →</p>
            </Link>
            <Link
              to="/contact"
              className="bg-gray-50 rounded-2xl p-6 text-center shadow-md hover:shadow-xl transition border-t-4 border-blue-900"
            >
              <p className="text-3xl mb-2">💙</p>
              <h3 className="font-extrabold text-blue-900 mb-1">I want to fund it</h3>
              <p className="text-gray-500 text-sm mb-3">
                Support projects with verified outcomes.
              </p>
              <p className="text-blue-900 font-bold text-sm">Give →</p>
            </Link>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default Network;
